import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Minus, Plus, ShoppingBag, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/kit";
import { api, errMsg, gbp, onColor } from "@/lib/api";
import { PageTitle } from "./HubPages";

const useCart = (slug) => {
  const key = `sd-cart-${slug}`;
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(cart));
  }, [key, cart]);
  return [cart, setCart];
};

const ProductCard = ({ p, color, onAdd }) => {
  const [opt, setOpt] = useState("");
  const soldOut = p.stock === 0;
  return (
    <div className="overflow-hidden rounded-lg border border-white/[0.08] bg-[#111827]" data-testid={`shop-product-${p.id}`}>
      <div className="aspect-square bg-white/5">{p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" /> : <ShoppingBag className="m-auto h-full w-12 text-slate-600" />}</div>
      <div className="space-y-3 p-5">
        <div className="flex items-start justify-between gap-2"><p className="font-display text-xl font-bold uppercase text-white">{p.name}</p><p className="font-mono font-bold" style={{ color: "var(--team-ink)" }}>{gbp(p.price)}</p></div>
        {p.description && <p className="text-sm text-slate-400">{p.description}</p>}
        {p.options.length > 0 && (
          <div className="flex flex-wrap gap-2">{p.options.map((o) => (
            <button key={o} type="button" onClick={() => setOpt(o)} className="rounded-full border px-3 py-1 text-xs font-bold" style={opt === o ? { background: color, color: onColor(color), borderColor: color } : { borderColor: "var(--hub-soft)", color: "var(--hub-text)" }} data-testid={`shop-option-${p.id}-${o}`}>{o}</button>
          ))}</div>
        )}
        <Button disabled={soldOut} className="w-full rounded-full font-bold uppercase" style={{ background: color, color: onColor(color) }}
          onClick={() => (p.options.length && !opt ? toast.error("Choose a size first") : onAdd(p, opt))} data-testid={`shop-add-${p.id}`}>{soldOut ? "Sold out" : "Add to basket"}</Button>
      </div>
    </div>
  );
};

const Basket = ({ cart, setCart, shop, slug, color }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", delivery: false });
  const [busy, setBusy] = useState(false);
  const qty = (i, d) => setCart(cart.map((c, j) => (j === i ? { ...c, qty: Math.max(1, Math.min(20, c.qty + d)) } : c)));
  const sub = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const fee = f.delivery ? shop.delivery_fee : 0;
  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/teams/${slug}/shop/checkout`, { ...f, items: cart.map((c) => ({ product_id: c.product_id, option: c.option, qty: c.qty })), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <aside className="h-fit rounded-lg border border-white/[0.08] bg-[#111827] p-6 lg:sticky lg:top-24" data-testid="shop-basket">
      <p className="font-display text-2xl font-bold uppercase text-white">Basket</p>
      {!cart.length && <p className="mt-3 text-sm text-slate-500">Your basket is empty.</p>}
      <div className="mt-3 space-y-3">{cart.map((c, i) => (
        <div key={`${c.product_id}-${c.option}`} className="flex items-center justify-between gap-2 text-sm" data-testid={`basket-item-${i}`}>
          <div className="min-w-0"><p className="truncate font-semibold text-white">{c.name}{c.option && ` · ${c.option}`}</p><p className="text-xs text-slate-500">{gbp(c.price)}</p></div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => qty(i, -1)} data-testid={`basket-minus-${i}`}><Minus className="h-3 w-3" /></Button>
            <span className="w-5 text-center font-mono text-white" data-testid={`basket-qty-${i}`}>{c.qty}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => qty(i, 1)} data-testid={`basket-plus-${i}`}><Plus className="h-3 w-3" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => setCart(cart.filter((_, j) => j !== i))} data-testid={`basket-remove-${i}`}><X className="h-3 w-3" /></Button>
          </div>
        </div>
      ))}</div>
      {cart.length > 0 && (
        <form onSubmit={checkout} className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="shop-checkout-form">
          <Field label="Name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="shop-name-input" /></Field>
          <Field label="Email"><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="shop-email-input" /></Field>
          <Field label="Phone (optional)"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="shop-phone-input" /></Field>
          {shop.delivery_fee > 0 && (
            <div className="grid grid-cols-2 gap-2 text-xs font-bold uppercase">{[[false, "Collect"], [true, `Deliver +${gbp(shop.delivery_fee)}`]].map(([v, l]) => (
              <button key={l} type="button" onClick={() => setF({ ...f, delivery: v })} className="rounded-full border px-3 py-2" style={f.delivery === v ? { background: color, color: onColor(color), borderColor: color } : { borderColor: "var(--hub-soft)", color: "var(--hub-text)" }} data-testid={`shop-delivery-${v ? "deliver" : "collect"}`}>{l}</button>
            ))}</div>
          )}
          {shop.note && <p className="text-xs text-slate-400" data-testid="shop-note">{shop.note}</p>}
          <p className="flex justify-between font-display text-xl font-bold text-white"><span>Total</span><span data-testid="shop-total">{gbp(sub + fee)}</span></p>
          <Button type="submit" disabled={busy} className="w-full rounded-full font-bold uppercase" style={{ background: color, color: onColor(color) }} data-testid="shop-checkout-btn">{busy ? "Opening secure checkout…" : "Pay securely"}</Button>
        </form>
      )}
    </aside>
  );
};

const OrderResult = ({ sessionId, color, onDone }) => {
  const [o, setO] = useState(null);
  useEffect(() => {
    let n = 0;
    const poll = () => api.get(`/public/shop/orders/${sessionId}`).then((r) => {
      setO(r.data);
      if (r.data.status === "pending" && n++ < 8) setTimeout(poll, 2000);
      else if (r.data.status === "paid") onDone();
    }).catch(() => setO({ status: "missing" }));
    poll();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!o) return null;
  const paid = ["paid", "fulfilled"].includes(o.status);
  return (
    <div className="mb-8 rounded-lg border p-6" style={{ borderColor: paid ? color : "var(--hub-soft)" }} data-testid="shop-order-result">
      {paid ? <p className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-white"><CheckCircle2 className="h-6 w-6" style={{ color: "var(--team-ink)" }} /> Order confirmed — {gbp(o.total)}</p>
        : <p className="text-slate-300">{o.status === "pending" ? "Still confirming your payment — if you completed it, your receipt email will arrive shortly. Refresh this page to check again." : "This payment wasn't completed — you haven't been charged."}</p>}
      {paid && <p className="mt-2 text-sm text-slate-400">A receipt is on its way to your inbox. {o.delivery ? "We'll post it to you soon." : "The club will let you know when it's ready to collect."}</p>}
    </div>
  );
};

export const ShopPage = ({ slug, color }) => {
  const [shop, setShop] = useState(null);
  const [cart, setCart] = useCart(slug);
  const [params] = useSearchParams();
  const order = params.get("order");
  useEffect(() => {
    api.get(`/public/teams/${slug}/shop`).then((r) => {
      setShop(r.data);
      const live = Object.fromEntries(r.data.products.map((p) => [p.id, p]));
      setCart((c) => c.filter((i) => live[i.product_id]).map((i) => ({ ...i, name: live[i.product_id].name, price: live[i.product_id].price })));
    }).catch(() => setShop({ products: [], closed: true }));
  }, [slug, setCart]);
  const add = (p, option) => {
    const i = cart.findIndex((c) => c.product_id === p.id && c.option === option);
    setCart(i >= 0 ? cart.map((c, j) => (j === i ? { ...c, qty: Math.min(20, c.qty + 1) } : c)) : [...cart, { product_id: p.id, name: p.name, price: p.price, option, qty: 1 }]);
    toast.success(`${p.name} added to basket`);
  };
  if (!shop) return null;
  return (
    <div data-testid="hub-shop">
      <PageTitle sub="Official kit and club merchandise">Club shop</PageTitle>
      {order && <OrderResult sessionId={order} color={color} onDone={() => setCart([])} />}
      {shop.closed ? <p className="text-slate-500">The shop is closed right now.</p> : (
        <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">{shop.products.map((p) => <ProductCard key={p.id} p={p} color={color} onAdd={add} />)}</div>
          <Basket cart={cart} setCart={setCart} shop={shop} slug={slug} color={color} />
        </div>
      )}
    </div>
  );
};
